import { IdentifierDTO, ZZMColor } from '@zazume/zzm-base';
import { DateVO } from '@zazume/zzm-utils';

import { BasicUnit } from '#/domain/unit/BasicUnit';

import { UTILITY_STATE_ALTERNATE_COLORS, UTILITY_STATE_COLORS } from './constants';
import {
  BasicApplication,
  SuppliesHolder,
  Utilities,
  UtilitiesChangeModeType,
  UtilitiesProvider,
  Utility,
  UtilityStatesType,
  UtilityTypeType
} from './types';

interface ConstructorParams {
  _id: IdentifierDTO;
  unit: BasicUnit;
  organizationId: IdentifierDTO;
  holder: SuppliesHolder;
  utilities: Utilities;
  application?: BasicApplication;
  requestBy?: IdentifierDTO;
  noManagement?: boolean;
  externalProvider?: UtilitiesProvider;
  preferredTimeToCall?: string;
  changeMode?: UtilitiesChangeModeType;
  laterManagementDismissed?: boolean;
  updatedAt: DateVO;
  createdAt: DateVO;
}

export class Supplies {
  _id: IdentifierDTO;
  unit: BasicUnit;
  organizationId: IdentifierDTO;
  holder: SuppliesHolder;
  utilities: Utilities;
  application?: BasicApplication;
  requestBy?: IdentifierDTO;
  noManagement?: boolean;
  externalProvider?: UtilitiesProvider;
  preferredTimeToCall?: string;
  changeMode?: UtilitiesChangeModeType;
  laterManagementDismissed?: boolean;
  updatedAt: DateVO;
  createdAt: DateVO;

  constructor(params: ConstructorParams) {
    this._id = params._id;
    this.unit = params.unit;
    this.holder = params.holder;
    this.organizationId = params.organizationId;
    this.utilities = params.utilities;
    this.application = params.application;
    this.requestBy = params.requestBy;
    this.noManagement = params.noManagement;
    this.externalProvider = params.externalProvider;
    this.preferredTimeToCall = params.preferredTimeToCall;
    this.changeMode = params.changeMode;
    this.laterManagementDismissed = params.laterManagementDismissed;
    this.updatedAt = params.updatedAt;
    this.createdAt = params.createdAt;
  }

  static fromRaw(raw: any): Supplies {
    return new Supplies({
      _id: raw._id,
      unit: BasicUnit.fromRaw(raw.unit),
      organizationId: raw.organizationId,
      holder: raw.holder,
      utilities: {
        electricity: Supplies.utilityFromRaw(raw.utilities.electricity),
        water: Supplies.utilityFromRaw(raw.utilities.water),
        gas: Supplies.utilityFromRaw(raw.utilities.gas),
        internet: Supplies.utilityFromRaw(raw.utilities.internet),
        insurance: Supplies.utilityFromRaw(raw.utilities.insurance),
        alarm: Supplies.utilityFromRaw(raw.utilities.alarm)
      },
      application: raw.application,
      requestBy: raw.requestBy,
      noManagement: raw.noManagement,
      externalProvider: Supplies.externalProviderFromRaw(raw.externalProvider),
      preferredTimeToCall: raw.preferredTimeToCall,
      changeMode: raw.changeMode,
      laterManagementDismissed: raw.laterManagementDismissed,
      updatedAt: raw.updatedAt && DateVO.fromPrimitive(raw.updatedAt),
      createdAt: DateVO.fromPrimitive(raw.createdAt)
    });
  }

  static utilityFromRaw(raw: any): Utility | undefined {
    if (!raw) {
      return undefined;
    }
    return {
      type: raw.type,
      defaultSelection: raw.defaultSelection,
      state: raw.state,
      activatedAt: raw.activatedAt && DateVO.fromPrimitive(raw.activatedAt),
      optionSelected: raw.optionSelected,
      externalProvider: Supplies.externalProviderFromRaw(raw.externalProvider)
    };
  }

  static externalProviderFromRaw(raw: any): UtilitiesProvider | undefined {
    if (!raw) {
      return undefined;
    }
    return {
      id: raw.id,
      error: raw.error,
      rawErrorMessage: raw.rawErrorMessage,
      provider: raw.provider,
      isInProgress: raw.isInProgress,
      issues: raw.issues,
      registeredAt: raw.registeredAt && DateVO.fromPrimitive(raw.registeredAt),
      tuioFallbackError: raw.tuioFallbackError
    };
  }

  static getStateColor(state: UtilityStatesType): ZZMColor | undefined {
    return UTILITY_STATE_COLORS[state];
  }

  getUtilityStateColor(utility: UtilityTypeType): ZZMColor | undefined {
    const state = this.utilities[utility]?.state;
    if (state) {
      return UTILITY_STATE_COLORS[state];
    }
    return undefined;
  }

  static getAlternateStateColor(state: UtilityStatesType): ZZMColor | undefined {
    return UTILITY_STATE_ALTERNATE_COLORS[state];
  }

  getUtilityAlternateStateColor(utility: UtilityTypeType): ZZMColor | undefined {
    const state = this.utilities[utility]?.state;
    if (state) {
      return UTILITY_STATE_ALTERNATE_COLORS[state];
    }
    return undefined;
  }

  getAllIssues(): string[] {
    return [...(this.externalProvider?.issues || []), ...(this.utilities?.insurance?.externalProvider?.issues || [])];
  }

  someUtilityIsInState(state: UtilityStatesType): boolean {
    return (
      this.utilities.alarm?.state === state ||
      this.utilities.electricity?.state === state ||
      this.utilities.gas?.state === state ||
      this.utilities.insurance?.state === state ||
      this.utilities.internet?.state === state ||
      this.utilities.water?.state === state
    );
  }

  everyUtilityIsInState(state: UtilityStatesType): boolean {
    return (
      this.utilities.alarm?.state === state &&
      this.utilities.electricity?.state === state &&
      this.utilities.gas?.state === state &&
      this.utilities.insurance?.state === state &&
      this.utilities.internet?.state === state &&
      this.utilities.water?.state === state
    );
  }

  getPendingUtilitiesToManageLater(): Record<UtilityTypeType, boolean> {
    const availableStates: UtilityStatesType[] = ['pending', 'dismissed'];
    const { water, electricity, gas, internet, alarm, insurance } = this.utilities;

    return {
      water: water?.state ? availableStates.includes(water?.state) : true,
      electricity: electricity?.state ? availableStates.includes(electricity.state) : true,
      gas: gas?.state ? availableStates.includes(gas.state) : true,
      internet: internet?.state ? availableStates.includes(internet.state) : true,
      alarm: alarm?.state ? availableStates.includes(alarm.state) : true,
      insurance: insurance?.state ? availableStates.includes(insurance.state) && !insurance.externalProvider : true
    };
  }

  getRequesterRole(): 'owner' | 'buildingManager' | undefined {
    if (!this.requestBy) {
      return undefined;
    }
    if (this.unit.ownerAccountIds?.includes(this.requestBy)) {
      return 'owner';
    }
    return 'buildingManager';
  }

  toUtilitiesArray(): Utility[] {
    const utilities: Utility[] = [];
    if (this.utilities.electricity) {
      utilities.push(this.utilities.electricity);
    }
    if (this.utilities.gas) {
      utilities.push(this.utilities.gas);
    }
    if (this.utilities.water) {
      utilities.push(this.utilities.water);
    }
    if (this.utilities.insurance) {
      utilities.push(this.utilities.insurance);
    }
    if (this.utilities.internet) {
      utilities.push(this.utilities.internet);
    }
    if (this.utilities.alarm) {
      utilities.push(this.utilities.alarm);
    }
    return utilities;
  }
}
